// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// Targets, the captive ball and the gate (M3-26, 07 §3.5).
//
// Four mechanisms that a player identifies entirely by ear, because most of the time they are
// looking somewhere else. A drop target going over, a standup being clipped, the captive ball
// taking a hit and a one-way gate flapping shut are four different pieces of information about
// where the ball is, and they have to be four different sounds rather than four volumes of one.
//
// Each recipe is the same three-part structure the whole layer uses — a filtered noise
// transient, a set of modal partials, and for anything sprung, a pitch-dropping twang — because
// that is what these objects physically are.
//
// **What is deliberately not here is the ball's own impact.** A ball striking a plastic target
// face already sounds, from the contact layer (M3-24), as `impactPlastic`. These cues are the
// *mechanism*: the face hitting its stop, the spring letting go, the switch blade closing. That
// split is why a drop target that is hit too softly to fall still makes a noise, and why the one
// that does fall makes two.

import {
  addNoiseBurst,
  addPartial,
  addPitchDrop,
  Biquad,
  fadeOut,
  normalise,
  samplesFor,
  type NoiseSource,
  type Samples,
} from './dsp'

/**
 * Spacing between the clacks of a bank reset, seconds.
 *
 * 07 §3.5: "the reset solenoid, then three clacks 18 ms apart — **genuinely three, not one**."
 * Every target in a bank is raised on the same simulation step, so all three arrive at the audio
 * layer together and would otherwise land on one instant and sum into a single louder clack. A
 * real bank's targets do not pop up in unison — one coil drives a common bar and the faces
 * arrive as fast as their own springs allow — and the stagger is the difference between hearing
 * a bank reset and hearing a click.
 */
export const BANK_CLACK_SPACING = 0.018

// --- drop target ----------------------------------------------------------------------------

const CLACK_HZ = 1900
const CLACK_Q = 2
const CLACK_MS = 9
/** The spring letting go: a short twang falling as the tension comes off. */
const TWANG_FROM_HZ = 620
const TWANG_TO_HZ = 300
const TWANG_GLIDE_MS = 14
const TWANG_DECAY_MS = 45
const TWANG_GAIN = 0.5
/** The face rattling in its slot afterwards. Quiet, and the reason it sounds like a mechanism. */
const SETTLE_HZ = 700
const SETTLE_MS = 26
const SETTLE_GAIN = 0.22

export function dropTargetLengthMs(): number {
  return TWANG_DECAY_MS * 1.15 + 8
}

/**
 * A drop target's mechanism, going over or coming back up.
 *
 * One recipe for both directions, because it is one mechanism: a plastic face hitting a stop
 * with a spring behind it. What differs between falling and resetting is what happens *around*
 * it — a ball impact before the fall, a coil before the reset — and both of those are their own
 * sounds.
 */
export function renderDropTargetClack(
  sampleRate: number,
  noise: NoiseSource,
  pitchScale = 1,
): Samples {
  const out = new Float32Array(samplesFor(dropTargetLengthMs(), sampleRate))

  addPitchDrop(
    out,
    sampleRate,
    TWANG_FROM_HZ * pitchScale,
    TWANG_TO_HZ * pitchScale,
    TWANG_GLIDE_MS,
    TWANG_GAIN,
    TWANG_DECAY_MS,
  )
  addNoiseBurst(
    out,
    sampleRate,
    noise,
    Biquad.bandpass(CLACK_HZ * pitchScale, CLACK_Q, sampleRate),
    1,
    CLACK_MS,
  )
  addNoiseBurst(
    out,
    sampleRate,
    noise,
    Biquad.bandpass(SETTLE_HZ * pitchScale, 1.1, sampleRate),
    SETTLE_GAIN,
    SETTLE_MS,
  )

  normalise(out)
  fadeOut(out, sampleRate)
  return out
}

// --- standup --------------------------------------------------------------------------------

const TICK_HZ = 3600
const TICK_MS = 4
/**
 * The switch blade, as two partials a hundred and eighty hertz apart.
 *
 * "A faint switch-blade buzz" is a beat, not a tone: two thin strips of metal ringing at nearly
 * the same pitch, which is what a leaf switch is made of. Detuning them by 180 Hz produces the
 * buzz directly out of the interference, and costs two partials rather than a modulator.
 */
const BLADE_A_HZ = 5200
const BLADE_B_HZ = 5380
const BLADE_DECAY_MS = 22

export function standupLengthMs(): number {
  return BLADE_DECAY_MS * 1.15 + 8
}

export function renderStandupTick(
  sampleRate: number,
  noise: NoiseSource,
  pitchScale = 1,
): Samples {
  const out = new Float32Array(samplesFor(standupLengthMs(), sampleRate))

  addPartial(out, sampleRate, BLADE_A_HZ * pitchScale, 0.3, BLADE_DECAY_MS)
  addPartial(out, sampleRate, BLADE_B_HZ * pitchScale, 0.25, BLADE_DECAY_MS)
  addNoiseBurst(
    out,
    sampleRate,
    noise,
    Biquad.bandpass(TICK_HZ * pitchScale, 2, sampleRate),
    1,
    TICK_MS,
  )

  normalise(out)
  fadeOut(out, sampleRate)
  return out
}

// --- captive ball ---------------------------------------------------------------------------

/**
 * Two tones, lower and heavier than the free-ball pair.
 *
 * A captive ball is struck through a rubber and is usually held on a short track or chain, so it
 * neither rings as brightly as two loose balls nor gets away afterwards. Lower partials and a
 * longer rattle is that difference; using `impactBall` here would tell the player they had a
 * second ball loose, which is a lie about the state of the game.
 */
const CAPTIVE_A_HZ = 3800
const CAPTIVE_B_HZ = 2600
const CAPTIVE_A_DECAY_MS = 9
const CAPTIVE_B_DECAY_MS = 13
/** The chain. Broadband, low-mid, and by far the longest part of the sound. */
const CHAIN_HZ = 1500
const CHAIN_DECAY_MS = 90
const CHAIN_GAIN = 0.3

export function captiveLengthMs(): number {
  return CHAIN_DECAY_MS * 1.15 + 8
}

export function renderCaptiveClick(
  sampleRate: number,
  noise: NoiseSource,
  pitchScale = 1,
): Samples {
  const out = new Float32Array(samplesFor(captiveLengthMs(), sampleRate))

  addPartial(out, sampleRate, CAPTIVE_A_HZ * pitchScale, 1, CAPTIVE_A_DECAY_MS)
  addPartial(out, sampleRate, CAPTIVE_B_HZ * pitchScale, 0.8, CAPTIVE_B_DECAY_MS)
  addNoiseBurst(
    out,
    sampleRate,
    noise,
    Biquad.bandpass(CHAIN_HZ * pitchScale, 1.2, sampleRate),
    CHAIN_GAIN,
    CHAIN_DECAY_MS,
  )

  normalise(out)
  fadeOut(out, sampleRate)
  return out
}

// --- gate -----------------------------------------------------------------------------------

/**
 * A one-way gate: a wire flap the ball pushes through, which then swings back and hits its stop.
 *
 * Not in 07 §3.5's prose but named in the format's own synthesis vocabulary as `gateRattle`, and
 * the core has emitted `EV_GATE_PASSED` since M1 with the crossing speed on it. A gate is the
 * quietest thing on this list and the most informative: it is how a player knows, without
 * looking, that the ball has committed to an orbit rather than come back.
 */
const GATE_A_HZ = 2600
const GATE_B_HZ = 3900
const GATE_A_DECAY_MS = 70
const GATE_B_DECAY_MS = 45
const GATE_TICK_HZ = 3000
const GATE_TICK_MS = 2.5

export function gateLengthMs(): number {
  return GATE_A_DECAY_MS * 1.15 + 8
}

export function renderGateRattle(
  sampleRate: number,
  noise: NoiseSource,
  pitchScale = 1,
): Samples {
  const out = new Float32Array(samplesFor(gateLengthMs(), sampleRate))

  addPartial(out, sampleRate, GATE_A_HZ * pitchScale, 1, GATE_A_DECAY_MS)
  addPartial(out, sampleRate, GATE_B_HZ * pitchScale, 0.35, GATE_B_DECAY_MS)
  addNoiseBurst(
    out,
    sampleRate,
    noise,
    Biquad.bandpass(GATE_TICK_HZ * pitchScale, 3, sampleRate),
    0.4,
    GATE_TICK_MS,
  )

  normalise(out)
  fadeOut(out, sampleRate)
  return out
}
